import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { Activity, Building2, ClipboardList, FileText, Send, ShieldCheck, ChevronRight, Clock3, GitBranch, History } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api, DAY_LABELS_SHORT, statusLabel, openDocumentBlob } from "../api";
import { Header } from "./Shell";
import "./SystemReleases.css";
import FacilitiesMap from "./FacilitiesMap";
import "./FacilityViewTabs.css";

export function Dashboard({ user }) {
  const [data, setData] = useState(null);

  const permissions = user?.permissions || [];
  const can = permission => permissions.includes(permission);

  useEffect(() => {
    api.get("/dashboard").then(r => setData(r.data));
  }, []);

  return <>
    <Header
      kicker="CENTRALE OPERATIVA / PANORAMICA"
      title={`Buongiorno, ${user.name.split(" ")[0]}.`}
      subtitle="Seleziona un’area per iniziare la consultazione operativa."
      showStatus={false}
    />

    <NavLink
      to="/novita"
      className="hero-alert system-updates-link"
      data-testid="dashboard-system-releases-link"
      aria-label="Consulta le novità e gli aggiornamenti di 118Finder"
    >
      <div className="hero-icon"><History size={28}/></div>
      <div className="system-updates-link-content">
        <span className="eyebrow">118FINDER · NOVITÀ E AGGIORNAMENTI</span>
        <h2>Scopri l’evoluzione di 118Finder.</h2>
        <p>Consulta le nuove funzionalità, i miglioramenti e la cronologia delle versioni del sistema.</p>
        <span className="system-updates-link-action">
          Esplora gli aggiornamenti <ChevronRight size={16}/>
        </span>
      </div>
      <div className="hero-code">SYS<br/><b>118</b></div>
    </NavLink>

    <section className="action-grid">
      {can("operative.facilities.read") &&
        <NavLink
          data-testid="dashboard-facility-search-link"
          to="/strutture"
          className="action-card action-red"
        >
          <span className="card-index">01 / RICERCA</span>
          <Building2 size={30}/>
          <h2>Dove posso<br/><em>inviare il paziente?</em></h2>
          <p>Cerca strutture e servizi con disponibilità verificata.</p>
          <ChevronRight className="card-arrow"/>
        </NavLink>
      }

      {can("operative.ai.use") &&
        <NavLink
          data-testid="dashboard-procedure-link"
          to="/procedure"
          className="action-card action-dark"
        >
          <span className="card-index">02 / KNOWLEDGE BASE</span>
          <ClipboardList size={30}/>
          <h2>Consulta<br/><em>procedure operative</em></h2>
          <p>Interroga la documentazione interna con citazioni.</p>
          <ChevronRight className="card-arrow"/>
        </NavLink>
      }

      {can("operative.algorithms.read") &&
        <NavLink
          data-testid="dashboard-algorithms-link"
          to="/algoritmi"
          className="action-card action-dark"
        >
          <span className="card-index">03 / ALGORITMI</span>
          <GitBranch size={30}/>
          <h2>Consulta<br/><em>algoritmi operativi</em></h2>
          <p>Accedi agli algoritmi decisionali disponibili.</p>
          <ChevronRight className="card-arrow"/>
        </NavLink>
      }
    </section>

    <div className="section-heading">
      <div>
        <span className="eyebrow">MONITORAGGIO SISTEMA</span>
        <h2>Stato della piattaforma</h2>
      </div>
      <span className="updated"><Clock3 size={14}/> Ultimo aggiornamento ora</span>
    </div>

    <div className="metric-grid">
      {[
        ["Strutture attive",data?.stats.facilities,"FACILITIES"],
        ["Servizi censiti",data?.stats.services,"SERVICES"],
        ["Procedure attive",data?.stats.documents,"DOCUMENTS"],
        ["Operatori attivi",data?.stats.users,"USERS"]
      ].map(([l,v,c])=>
        <div className="metric" key={c} data-testid={`metric-${c.toLowerCase()}`}>
          <span>{l}</span>
          <b>{v??"—"}</b>
          <small>{c} · DEMO</small>
        </div>
      )}
    </div>
  </>;
}

export function FacilitySearch() {
  const [view, setView] = useState("search");
  const [q, setQ] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const search = async e => {
    e.preventDefault();
    setLoading(true);
    try { setResult((await api.post("/ai/facility-search", { question: q })).data); }
    finally { setLoading(false); }
  };
  return <>
    <Header kicker="OPERATIVO / STRUTTURE" title="Trova struttura / servizio" subtitle="Ricerca assistita e consultazione geografica delle strutture."/>
    <div className="facility-view-tabs" role="tablist" aria-label="Visualizzazione strutture">
      <button type="button" role="tab" aria-selected={view==="search"} className={view==="search"?"facility-view-tab active":"facility-view-tab"} onClick={()=>setView("search")} data-testid="facility-search-view-button">Ricerca assistita</button>
      <button type="button" role="tab" aria-selected={view==="map"} className={view==="map"?"facility-view-tab active":"facility-view-tab"} onClick={()=>setView("map")} data-testid="facility-map-view-button">Mappa strutture</button>
    </div>
    {view==="map" ? <FacilitiesMap/> : <><div className="query-layout"><section className="query-box"><div className="query-box-head"><span className="eyebrow">RICERCA ASSISTITA</span><span className="ai-badge"><span/> INTERPRETAZIONE IA</span></div><form onSubmit={search}><textarea data-testid="facility-search-input" value={q} onChange={e=>setQ(e.target.value)} rows="4"/><button data-testid="facility-search-submit-button" className="primary-btn" disabled={loading}>{loading?"Elaborazione…":"Cerca disponibilità"}<Send size={16}/></button></form><div className="suggestions"><span>Prova:</span>{["in provincia BO","in provincia MO","in provincia FE","ortopedia al Maggiore","strutture in provincia di BO","pronto soccorso a Ferrara"].map(x=><button data-testid={`suggestion-${x.replaceAll(" ","-")}-button`} key={x} onClick={()=>setQ(x)}>{x}</button>)}</div></section><aside className="guardrail"><ShieldCheck size={20}/><b>Ricerca deterministica</b><p>L’IA interpreta la domanda, ma strutture, servizi, area geografica, giorni ed orari sono letti esclusivamente dal database.</p></aside></div>{result&&<section className="results-section"><div className="result-header"><div><span className="eyebrow">RISULTATI VERIFICATI</span><h2>{result.results.length} risultati corrispondenti</h2></div><span className="interpretation">{result.interpretation}</span></div>{result.results.length===0&&<div className="empty-results" data-testid="facility-empty-results">Nessun risultato per questa combinazione. Verifica il servizio, la struttura, l'area o l'orario.</div>}<div className="result-list">{result.results.map((r,i)=><article className={`facility-result status-${r.status||"aperto"}`} key={i} data-testid={`facility-result-${i}`}><div className="facility-icon"><Building2 size={20}/></div><div className="facility-main"><h3>{r.facility.name} <span>{r.facility.type}</span></h3><p>{r.facility.address}, {r.facility.city} ({r.facility.province}) · {r.facility.phone}</p>{r.service?<><div className="result-tags"><strong>{r.service}</strong><span className={`status-badge status-${r.status||"aperto"}`} data-testid={`result-status-${i}`}>{statusLabel(r.status||"aperto")}</span></div><div className="availability-detail" data-testid={`result-availability-${i}`}>{r.availability?.h24
  ? <span className="line"><Clock3 size={13}/> H24 · tutti i giorni</span>
  : (
      Array.isArray(r.availability?.schedule) &&
      r.availability.schedule.length
        ? <div className="availability-slots">
            {r.availability.schedule.map((slot,slotIndex)=>
              <div className="availability-slot" key={slotIndex}>
                <span className="line">
                  <Clock3 size={13}/>
                  Orario {slot.start} — {slot.end}
                </span>
                <div className="day-chips">
                  {DAY_LABELS_SHORT.map((d,idx)=>
                    <span
                      key={idx}
                      className={(slot.days||[]).includes(idx)?"chip on":"chip"}
                    >
                      {d}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        : <>
            <span className="line">
              <Clock3 size={13}/>
              Orario {r.availability?.start} — {r.availability?.end}
            </span>
            <div className="day-chips">
              {DAY_LABELS_SHORT.map((d,idx)=>
                <span
                  key={idx}
                  className={(r.availability?.days||[]).includes(idx)?"chip on":"chip"}
                >
                  {d}
                </span>
              )}
            </div>
          </>
    )}{r.availability?.notes&&<span className="line note">Note: {r.availability.notes}</span>}{(r.availability?.exceptions||[]).length>0&&<div className="exception-line" data-testid={`result-exceptions-${i}`}><b>Eccezioni programmate:</b>{r.availability.exceptions.map(x=><span key={x.id} className={r.active_exception?.id===x.id?"exception-chip on":"exception-chip"}>{new Date(x.start_date).toLocaleDateString("it-IT")} — {new Date(x.end_date).toLocaleDateString("it-IT")}{x.reason?` · ${x.reason}`:""}</span>)}</div>}</div></>:<div className="result-tags"><span className="availability info"><i/> Struttura corrispondente all'area geografica</span></div>}</div><div className="result-meta"><span>AGGIORNATO</span><b>{new Date(r.availability?.updated_at||r.facility.updated_at||Date.now()).toLocaleDateString("it-IT")}</b></div></article>)}</div><div className="notice"><ShieldCheck size={16}/>{result.notice}</div></section>}</>}
  </>;
}

export function ProcedureSearch() {
  const [q, setQ] = useState("Qual è la procedura prevista per un sospetto ictus?");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const submit = async e => {
    e.preventDefault();
    setLoading(true);
    try { setResult((await api.post("/ai/procedure-query", { question: q })).data); }
    finally { setLoading(false); }
  };
  return <><Header kicker="OPERATIVO / KNOWLEDGE BASE" title="Procedure operative" subtitle="Consulta esclusivamente la documentazione attiva autorizzata."/><div className="query-layout"><section className="query-box procedure-box"><div className="query-box-head"><span className="eyebrow">DOCUMENTAZIONE INTERNA</span><span className="ai-badge blue"><span/> RAG ATTIVO</span></div><form onSubmit={submit}><textarea data-testid="procedure-search-input" value={q} onChange={e=>setQ(e.target.value)} rows="4"/><button data-testid="procedure-search-submit-button" className="primary-btn" disabled={loading}>{loading?"Recupero fonti…":"Consulta procedure"}<Send size={16}/></button></form><div className="suggestions"><span>Argomenti indicizzati:</span><button data-testid="procedure-suggestion-stroke-button" onClick={()=>setQ("procedura Stroke e ictus")}>Stroke / ictus</button><button data-testid="procedure-suggestion-elicottero-button" onClick={()=>setQ("attivazione elisoccorso")}>Elisoccorso</button></div></section><aside className="guardrail blue-guard"><FileText size={20}/><b>Fonti obbligatorie</b><p>Ogni risposta mostra titolo, versione, pagina e sezione del documento recuperato.</p></aside></div>{result&&<section className="answer-layout"><article className="answer-card"><div className="answer-label"><span className="eyebrow">RISPOSTA DOCUMENTALE</span><span className="verified"><ShieldCheck size={14}/> VERIFICATA SU FONTI</span></div><div className="answer-text" data-testid="procedure-answer"><ReactMarkdown remarkPlugins={[remarkGfm]} components={{
  p: ({children})=><p>{React.Children.map(children,child=>typeof child==="string"?child.split(/(\[pag\.\s*\d+\])/g).map((part,i)=>/^\[pag\.\s*\d+\]$/.test(part)?<sup key={i} className="cite-ref">{part}</sup>:part):child)}</p>,
  li: ({children})=><li>{React.Children.map(children,child=>typeof child==="string"?child.split(/(\[pag\.\s*\d+\])/g).map((part,i)=>/^\[pag\.\s*\d+\]$/.test(part)?<sup key={i} className="cite-ref">{part}</sup>:part):child)}</li>,
  td: ({children})=><td>{React.Children.map(children,child=>typeof child==="string"?child.split(/(\[pag\.\s*\d+\])/g).map((part,i)=>/^\[pag\.\s*\d+\]$/.test(part)?<sup key={i} className="cite-ref">{part}</sup>:part):child)}</td>,
}}>{result.answer}</ReactMarkdown></div><div className="notice"><ShieldCheck size={16}/>{result.notice}</div></article><aside className="sources-card"><span className="eyebrow">FONTI ({result.citations.length})</span>{result.citations.map((c,i)=><div className="source" key={i} data-testid={`procedure-source-${i}`}><span className="source-index">pag. {c.page}</span><div><b>{c.title}</b><span>Versione {c.version}</span><small>{c.section}</small>{c.document_id&&<button type="button" data-testid={`procedure-source-open-${i}`} className="row-btn source-open" onClick={()=>openDocumentBlob(c.document_id)}><FileText size={12}/> Apri documento</button>}</div></div>)}</aside></section>}</>;
}

export function Profile({ user }) {
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);
  const [pwMsg, setPwMsg] = useState("");
  const [pwErr, setPwErr] = useState("");
  const savePw = async e => {
    e.preventDefault(); setPwErr(""); setPwMsg("");
    if (pw.next.length < 8) { setPwErr("La nuova password deve contenere almeno 8 caratteri."); return; }
    if (pw.next !== pw.confirm) { setPwErr("La conferma non coincide con la nuova password."); return; }
    setSavingPw(true);
    try { await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next }); setPwMsg("Password aggiornata. Al prossimo login utilizza la nuova."); setPw({ current: "", next: "", confirm: "" }); }
    catch (error) { setPwErr(error.response?.data?.detail || "Aggiornamento non riuscito"); }
    finally { setSavingPw(false); }
  };
  return <>
    <Header showStatus={false} kicker="AREA PERSONALE" title="Profilo utente" subtitle="Consulta i dati del tuo account e gestisci la password di accesso."/>
    <div className="profile-grid">
      <section className="user-create-form" data-testid="profile-name-form">
        <div className="user-create-heading"><span className="eyebrow">DATI ACCOUNT</span><b>Anagrafica</b><small>I dati anagrafici sono gestiti dagli amministratori.</small></div>
        <div className="user-create-grid">
          <label>Email<input data-testid="profile-email-input" value={user?.email || ""} disabled readOnly/></label>
          <label>Ruolo<input data-testid="profile-role-input" value={user?.role_name || user?.role || ""} disabled readOnly/></label>
          <label className="span-2">Nome e cognome<input data-testid="profile-name-input" value={user?.name || ""} disabled readOnly/></label>
        </div>
      </section>
      <form className="user-create-form" data-testid="profile-password-form" onSubmit={savePw}>
        <div className="user-create-heading"><span className="eyebrow">SICUREZZA</span><b>Cambio password</b><small>Utilizza almeno 8 caratteri. Dopo il cambio, i vecchi token restano validi fino alla scadenza.</small></div>
        <div className="user-create-grid">
          <label className="span-2">Password attuale<input data-testid="profile-current-password-input" type="password" required value={pw.current} onChange={e=>setPw(prev=>({...prev,current:e.target.value}))}/></label>
          <label>Nuova password<input data-testid="profile-new-password-input" type="password" required minLength="8" value={pw.next} onChange={e=>setPw(prev=>({...prev,next:e.target.value}))}/></label>
          <label>Conferma nuova password<input data-testid="profile-confirm-password-input" type="password" required minLength="8" value={pw.confirm} onChange={e=>setPw(prev=>({...prev,confirm:e.target.value}))}/></label>
        </div>
        {pwErr&&<div className="error" data-testid="profile-password-error">{pwErr}</div>}
        {pwMsg&&<div className="success" data-testid="profile-password-success">{pwMsg}</div>}
        <div className="user-create-actions"><button data-testid="profile-password-submit-button" className="primary-btn" disabled={savingPw} type="submit">{savingPw?"Aggiornamento…":"Aggiorna password"}</button></div>
      </form>
    </div>
  </>;
}

export function UploadBridge() {
  useEffect(() => {
    let uploading = false;

    const notifyUploadState = active => {
      window.dispatchEvent(new CustomEvent(
        "documents:upload-state",
        { detail: { uploading: active } }
      ));
    };

    const upload = async event => {
      const input = event.target;
      if (input?.dataset?.testid !== "document-upload-input" || !input.files?.[0]) return;
      if (uploading) return;

      uploading = true;
      input.disabled = true;
      notifyUploadState(true);

      const file = input.files[0];
      const identifier = window.prompt("Identificativo del documento (opzionale)") || "";
      const form = new FormData();
      form.append("file", file);
      form.append("title", file.name.replace(/\.[^/.]+$/, ""));
      form.append("category", "");
      form.append("version", "1.0");
      form.append("identifier", identifier);
      try {
        await api.post("/admin/documents", form);
        window.alert("Documento caricato e indicizzato");
        window.dispatchEvent(new CustomEvent("documents:updated"));
      }
      catch (error) {
        window.alert(error.response?.data?.detail || "Upload non riuscito");
      }
      finally {
        input.value = "";
        input.disabled = false;
        uploading = false;
        notifyUploadState(false);
      }
    };
    document.addEventListener("change", upload);
    return () => document.removeEventListener("change", upload);
  }, []);
  return null;
}
